import {
    createTableColumn,
    TableColumnDefinition,
    TableColumnSizingOptions,
    TableCellLayout,
    Button,
    Tooltip,
} from '@fluentui/react-components';
import {
    bundleIcon,
    Delete24Filled,
    Delete24Regular,
    Edit24Filled,
    Edit24Regular,
} from '@fluentui/react-icons';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

export interface IConfigRow {
    id: string;
    value: string;
}

const EditIconBundle = bundleIcon(Edit24Filled, Edit24Regular);
const DeleteIconBundle = bundleIcon(Delete24Filled, Delete24Regular);

export const defaultColumnSizingOptions: TableColumnSizingOptions = {
    value: {
        minWidth: 200,
        idealWidth: 800,
    },
    actions: {
        minWidth: 100,
        idealWidth: 100,
    },
} as const;

export const getColumnSizingOptions = (columnId: string, isActionColumn: boolean = false): TableColumnSizingOptions[string] | undefined => {
    if (isActionColumn) {
        return {
            minWidth: 100,
            idealWidth: 100,
        };
    }
    return {
        minWidth: 200,
        idealWidth: 800,
    };
};

export type RowAction<R extends IConfigRow = IConfigRow> = (row: R) => void;

interface UseDefaultColumnsParams<R extends IConfigRow> {
    valueHeader: string;
    onEdit?: RowAction<R>;
    onDelete?: RowAction<R>;
}

export const useDefaultColumns = <R extends IConfigRow>({
    valueHeader,
    onEdit,
    onDelete,
}: UseDefaultColumnsParams<R>) => {
    const { t } = useTranslation();

    const columns = useMemo<TableColumnDefinition<R>[]>(
        () => [
            createTableColumn<R>({
                columnId: 'value',
                compare: (a, b) =>
                    a.value.localeCompare(b.value, undefined, {
                        sensitivity: 'base',
                    }),
                renderHeaderCell: () => {
                    return valueHeader;
                },
                renderCell: (item) => {
                    return item.value;
                },
            }),
            createTableColumn<R>({
                columnId: 'actions',
                renderHeaderCell: () => {
                    return <></>;
                },
                renderCell: (item) => {
                    return (
                        <TableCellLayout>
                            {onEdit && (
                                <Tooltip
                                    content={t('Tooltip_Edit')}
                                    relationship="label"
                                >
                                    <Button
                                        appearance="transparent"
                                        icon={<EditIconBundle />}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            if (onEdit) {
                                                onEdit(item);
                                            }
                                        }}
                                    />
                                </Tooltip>
                            )}
                            {onDelete && (
                                <Tooltip
                                    content={t('Tooltip_Delete')}
                                    relationship="label"
                                >
                                    <Button
                                        appearance="transparent"
                                        icon={<DeleteIconBundle />}
                                        onClick={() => {
                                            if (onDelete) {
                                                onDelete(item);
                                            }
                                        }}
                                    />
                                </Tooltip>
                            )}
                        </TableCellLayout>
                    );
                },
            }),
        ],
        [valueHeader, onEdit, onDelete, t]
    );

    return columns;
};

